// VoiceModeToggle: scelta dell'utente fra modalità testo e voce.
// Mostrato in StartPage post-consensi quando voice_enabled=true dal branding.

import { useTranslation } from 'react-i18next';

export type ChatMode = 'text' | 'voice';

interface VoiceModeToggleProps {
  value: ChatMode;
  onChange: (mode: ChatMode) => void;
  disabled?: boolean;
}

export function VoiceModeToggle({ value, onChange, disabled }: VoiceModeToggleProps) {
  const { t } = useTranslation();

  return (
    <fieldset
      className="rounded-md border p-4"
      style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
    >
      <legend className="mono px-2 text-xs" style={{ color: 'var(--mute)' }}>
        {t('chat.voiceModeTitle', { defaultValue: 'Come vuoi parlare con Audiq?' })}
      </legend>
      <div className="grid grid-cols-2 gap-2">
        <ModeOption
          active={value === 'text'}
          onClick={() => onChange('text')}
          disabled={disabled}
          title={t('chat.voiceModeText', { defaultValue: 'Testo' })}
          subtitle={t('chat.voiceModeTextDescr', {
            defaultValue: 'Scrivi i messaggi a Audiq',
          })}
          icon="text"
        />
        <ModeOption
          active={value === 'voice'}
          onClick={() => onChange('voice')}
          disabled={disabled}
          title={t('chat.voiceModeVoice', { defaultValue: 'Voce' })}
          subtitle={t('chat.voiceModeVoiceDescr', {
            defaultValue: 'Parla, Audiq ti risponde a voce',
          })}
          icon="mic"
        />
      </div>
    </fieldset>
  );
}

function ModeOption({
  active,
  onClick,
  disabled,
  title,
  subtitle,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  title: string;
  subtitle: string;
  icon: 'text' | 'mic';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className="flex flex-col items-start gap-1 rounded-md border p-3 text-start transition disabled:opacity-50"
      style={{
        background: active ? 'var(--soft)' : 'var(--paper)',
        borderColor: active ? 'var(--primary)' : 'var(--line)',
        color: 'var(--ink)',
      }}
    >
      <div className="flex items-center gap-2">
        {icon === 'mic' ? <MicGlyph /> : <TextGlyph />}
        <span className="font-semibold">{title}</span>
      </div>
      <span className="text-xs" style={{ color: 'var(--mute)' }}>
        {subtitle}
      </span>
    </button>
  );
}

function MicGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TextGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 6h16M4 12h16M4 18h10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
